import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { pageApi } from '../../services/api';
import { slugify } from '../../utils/slug';

export default function PagesPage() {
  const [pages, setPages] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 15 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', content: '', meta_title: '', meta_description: '', meta_keywords: '', status: 'draft', order: 0 });
  const [saving, setSaving] = useState(false);

  const fetchPages = async (page = 1) => {
    setLoading(true);
    try {
      const res = await pageApi.list({ search, per_page: 15, page });
      const d = res.data.data;
      setPages(d?.data || d || []);
      setPagination({ current_page: d?.current_page || 1, last_page: d?.last_page || 1, total: d?.total || 0, per_page: d?.per_page || 15 });
    } catch (err) {
      setError('Gagal memuat halaman.');
    } finally {
      setLoading(false);
    }
  };

  const pagesArr = []; for (let i = Math.max(1, pagination.current_page - 2); i <= Math.min(pagination.last_page, pagination.current_page + 2); i++) { pagesArr.push(i); }

  useEffect(() => { fetchPages(); }, [search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', slug: '', content: '', meta_title: '', meta_description: '', meta_keywords: '', status: 'draft', order: 0 });
    setShowModal(true);
  };

  const openEdit = (page) => {
    setEditing(page);
    setForm({ title: page.title, slug: page.slug, content: page.content || '', meta_title: page.meta_title || '', meta_description: page.meta_description || '', meta_keywords: page.meta_keywords || '', status: page.status, order: page.order });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await pageApi.update(editing.id, form);
      } else {
        await pageApi.create(form);
      }
      setShowModal(false);
      fetchPages(pagination.current_page);
    } catch (err) {
      alert('Gagal menyimpan: ' + (err.response?.data?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus halaman ini?')) return;
    try {
      await pageApi.delete(id);
      fetchPages(pagination.current_page);
    } catch (err) {
      alert('Gagal menghapus.');
    }
  };

  return (
    <AdminLayout active="Halaman">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-gray-800 mb-1">Halaman</h1>
          <button onClick={() => setShowInfoModal(true)} className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm" title="Cara pakai module halaman">Info</button>
        </div>
        <button onClick={openCreate} className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors">+ Tambah Halaman</button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <input type="text" placeholder="Cari halaman..." value={search} onChange={e => setSearch(e.target.value)} className="w-full max-w-sm px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">{error}</div>
        ) : pages.length === 0 ? (
          <div className="p-8 text-center text-gray-400">Belum ada halaman.</div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-6 py-3 font-medium text-gray-500">Judul</th>
                  <th className="px-6 py-3 font-medium text-gray-500">Slug</th>
                  <th className="px-6 py-3 font-medium text-gray-500">Status</th>
                  <th className="px-6 py-3 font-medium text-gray-500">Diperbarui</th>
                  <th className="px-6 py-3 font-medium text-gray-500">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pages.map(page => (
                  <tr key={page.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{page.title}</td>
                    <td className="px-6 py-4 text-gray-500">/{page.slug}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${page.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{page.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{new Date(page.updated_at).toLocaleDateString('id-ID')}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(page)} className="text-primary hover:underline text-xs">Edit</button>
                        <button onClick={() => handleDelete(page.id)} className="text-red-500 hover:underline text-xs">Hapus</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.last_page > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">Total {pagination.total} data · Halaman {pagination.current_page} dari {pagination.last_page}</span>
              <div className="flex items-center gap-1">
                <button disabled={pagination.current_page === 1} onClick={() => fetchPages(pagination.current_page - 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">« Prev</button>
                {pagesArr.map(p => (<button key={p} onClick={() => fetchPages(p)} className={`px-3 py-1 text-sm rounded ${p === pagination.current_page ? 'bg-brand text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{p}</button>))}
                <button disabled={pagination.current_page === pagination.last_page} onClick={() => fetchPages(pagination.current_page + 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">Next »</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {showInfoModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold">Panduan CRUD Halaman</h2>
              <button onClick={() => setShowInfoModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-6 space-y-4 text-sm text-gray-700">
              <div className="rounded-xl border border-gray-200 p-4 bg-gray-50">
                <h3 className="font-semibold text-gray-900 mb-2">Cara membuat halaman baru</h3>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Buka menu Halaman di admin.</li>
                  <li>Klik tombol <span className="font-semibold">Tambah Halaman</span>.</li>
                  <li>Isi judul halaman, misalnya <span className="font-semibold">Tentang Kami</span>.</li>
                  <li>Buat slug / alamat halaman, misalnya <span className="font-semibold">tentang-kami</span>.</li>
                  <li>Tulis isi konten halaman di editor.</li>
                  <li>Pilih status <span className="font-semibold">Published</span> kalau ingin langsung tampil di website.</li>
                  <li>Klik <span className="font-semibold">Simpan</span>.</li>
                </ol>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Apa yang bisa dilakukan?</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="font-semibold">Create</span> = buat halaman baru</li>
                  <li><span className="font-semibold">Read</span> = lihat daftar halaman</li>
                  <li><span className="font-semibold">Update</span> = edit halaman yang sudah ada</li>
                  <li><span className="font-semibold">Delete</span> = hapus halaman</li>
                </ul>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Field penting</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="font-semibold">Judul</span>: nama halaman yang muncul</li>
                  <li><span className="font-semibold">Slug</span>: alamat URL halaman, tanpa spasi</li>
                  <li><span className="font-semibold">Konten</span>: isi halaman</li>
                  <li><span className="font-semibold">Meta Title</span>: judul SEO</li>
                  <li><span className="font-semibold">Meta Description</span>: deskripsi SEO</li>
                  <li><span className="font-semibold">Status</span>: Draft atau Published</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold">{editing ? 'Edit Halaman' : 'Tambah Halaman'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Judul</label>
                  <input type="text" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug (tanpa spasi) *</label>
                  <input type="text" required value={form.slug} onChange={e => setForm({...form, slug: slugify(e.target.value)})} pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$" title="Slug tidak boleh mengandung spasi atau huruf besar" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" />
                </div>
              </div>
              <div>
                <RichTextEditor
                  label="Konten (HTML)"
                  value={form.content}
                  onChange={(val) => setForm({...form, content: val})}
                  height={250}
                  placeholder="Tulis konten halaman di sini..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                  <input type="text" value={form.meta_title} onChange={e => setForm({...form, meta_title: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                <textarea rows={2} value={form.meta_description} onChange={e => setForm({...form, meta_description: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Keywords</label>
                <input type="text" value={form.meta_keywords} onChange={e => setForm({...form, meta_keywords: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Urutan</label>
                <input type="number" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Batal</button>
                <button type="submit" disabled={saving} className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark disabled:opacity-50">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}